/**
 * OperationalChecklist — Checklist with progress bar for execution cockpit
 */
import React, { useMemo } from 'react';
import { Check, Circle, XCircle } from 'lucide-react';

interface ChecklistItem {
  id: string;
  label: string;
  required?: boolean;
}

interface OperationalChecklistProps {
  items: ChecklistItem[];
  checklistState: Record<string, boolean>;
  onToggle: (itemId: string) => void;
  readOnly?: boolean;
}

export const OperationalChecklist: React.FC<OperationalChecklistProps> = ({
  items,
  checklistState,
  onToggle,
  readOnly,
}) => {
  const stats = useMemo(() => {
    const total = items.length;
    const done = items.filter(i => checklistState[i.id]).length;
    const requiredTotal = items.filter(i => i.required).length;
    const requiredDone = items.filter(i => i.required && checklistState[i.id]).length;
    const percent = total > 0 ? Math.round((done / total) * 100) : 100;
    return { total, done, requiredTotal, requiredDone, percent };
  }, [items, checklistState]);

  if (items.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      {/* Progress bar */}
      <div className="flex items-center gap-2">
        <span className="text-[9px] font-mono font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
          Progresso: {stats.percent}%
        </span>
        <div className="flex-1 h-[6px] rounded-[3px] bg-[#1a1a1a] overflow-hidden">
          <div
            className={`h-full rounded-[3px] transition-all duration-300 ${
              stats.percent === 100 ? 'bg-[#50a050]' : stats.percent >= 50 ? 'bg-[#a09040]' : 'bg-[#505050]'
            }`}
            style={{ width: `${stats.percent}%` }}
          />
        </div>
        <span className="text-[9px] font-mono tabular-nums text-[var(--text-muted)]">
          {stats.done}/{stats.total}
        </span>
      </div>

      {/* Checklist items */}
      <div className="flex flex-col gap-[3px]">
        {items.map(item => {
          const isDone = checklistState[item.id] ?? false;
          const isRequired = item.required;
          const isMissing = isRequired && !isDone;

          return (
            <button
              key={item.id}
              type="button"
              onClick={() => !readOnly && onToggle(item.id)}
              disabled={readOnly}
              className={`flex items-center gap-2 rounded-[3px] border px-3 py-[6px] text-left transition-colors duration-75 ${
                isDone
                  ? 'border-[#204a20] bg-[#121e12] hover:bg-[#152215]'
                  : isMissing
                    ? 'border-[#4a2020] bg-[#1e1212] hover:bg-[#221515]'
                    : 'border-[var(--border-default)] bg-[var(--bg-surface)] hover:bg-[var(--bg-elevated)]'
              } ${readOnly ? 'cursor-default opacity-70' : 'cursor-pointer'}`}
            >
              {isDone ? (
                <Check size={12} className="shrink-0 text-[#50a050]" />
              ) : isMissing ? (
                <XCircle size={12} className="shrink-0 text-[#a05050]" />
              ) : (
                <Circle size={12} className="shrink-0 text-[var(--text-muted)]" />
              )}
              <span className={`text-[11px] flex-1 ${
                isDone ? 'text-[#70c070] line-through' : 'text-[var(--text-primary)]'
              }`}>
                {item.label}
              </span>
              {isRequired && !isDone && (
                <span className="text-[8px] font-bold uppercase text-[#a05050]">obrigatório</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
